import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowPathIcon, MicrophoneIcon, StopIcon } from "@heroicons/react/24/outline";
import useChunkedRecorderUpload, { TIMESLICE_MS } from "../../hooks/useChunkedRecorderUpload";

/**
 * Records a spoken answer, streaming it to the server as it goes.
 *
 * This used to buffer the whole take in the tab and hand the parent one Blob to
 * POST when the respondent moved on. That capped answers at 30 minutes and made
 * any dropped connection lose the recording outright — there was nothing to
 * resume from. It now shares the chunked, resumable uploader with the session
 * recording: every timeslice goes straight to the server, in order, with
 * per-chunk retry, so there is no duration limit and a lost connection costs
 * seconds rather than the whole answer.
 */

/**
 * Mono Opus at 32kbps — comfortably intelligible for speech, and small enough
 * that a chunk uploads quickly on a poor connection (~40KB per 10s timeslice).
 */
const AUDIO_BITS_PER_SECOND = 32_000;

/**
 * Local copy retained for playback only.
 *
 * ponytail: hard byte cap rather than streaming playback from the server.
 * ~100 minutes at this bitrate; past it the take is still uploaded, we just
 * stop holding it in the tab and drop the player. Swap for a signed-URL
 * playback endpoint if respondents actually need to review long answers.
 */
const PREVIEW_MAX_BYTES = 25 * 1024 * 1024;

function pickMimeType() {
    if (typeof MediaRecorder === "undefined") return "";
    const candidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
    ];
    return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || "";
}

function formatTime(seconds) {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
}

export default function VoiceAnswerRecorder({ responseUuid, questionUuid, value, onChange, disabled = false }) {
    const { t } = useTranslation();
    const [recording, setRecording] = useState(false);
    const [finalising, setFinalising] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [error, setError] = useState(null);
    const [warning, setWarning] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);

    const entryRef = useRef(null);
    const tickRef = useRef(null);
    const elapsedRef = useRef(0);
    // Retained only up to PREVIEW_MAX_BYTES; independent of what is uploaded.
    const previewChunksRef = useRef([]);
    const previewBytesRef = useRef(0);

    const { beginTrack, finishTrack, stopTrack } = useChunkedRecorderUpload({
        onDegraded: (message) => setWarning(message),
    });

    useEffect(() => {
        if (!value?.previewBlob) {
            setPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(value.previewBlob);
        setPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [value]);

    // Tear down on unmount without finalising: an unmount is not a finished
    // answer, and the server-side sweeper reclaims the temp file.
    useEffect(() => () => {
        clearInterval(tickRef.current);
        if (entryRef.current) stopTrack(entryRef.current, { finalize: false });
    }, [stopTrack]);

    const start = async () => {
        setError(null);
        setWarning(null);
        previewChunksRef.current = [];
        previewBytesRef.current = 0;

        let stream;
        try {
            stream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true },
            });
        } catch (e) {
            setError(t("Voice.PermissionDenied", "Microphone access was denied."));
            return;
        }

        const mimeType = pickMimeType();

        let entry;
        try {
            entry = await beginTrack(responseUuid, "voice", stream, {
                mimeType: mimeType || undefined,
                audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
                questionUuid,
            });
        } catch (e) {
            entry = null;
        }

        if (!entry) {
            stream.getTracks().forEach((track) => track.stop());
            setError(t("Voice.CouldNotStart", "Could not start recording. Please check your connection and try again."));
            return;
        }

        // Keep a bounded local copy for playback, alongside (not instead of) the
        // upload — the uploader already consumed the same chunk.
        const upstream = entry.recorder.ondataavailable;
        entry.recorder.ondataavailable = (event) => {
            upstream(event);
            if (event.data?.size && previewBytesRef.current + event.data.size <= PREVIEW_MAX_BYTES) {
                previewChunksRef.current.push(event.data);
                previewBytesRef.current += event.data.size;
            }
        };

        entryRef.current = entry;
        setRecording(true);
        setElapsed(0);
        elapsedRef.current = 0;

        tickRef.current = setInterval(() => {
            elapsedRef.current += 1;
            setElapsed(elapsedRef.current);
        }, 1000);
    };

    const stop = useCallback(async () => {
        const entry = entryRef.current;
        clearInterval(tickRef.current);
        setRecording(false);

        if (!entry) return;

        setFinalising(true);

        const duration = Math.round(elapsedRef.current);
        const ok = await finishTrack(entry, { durationSeconds: duration });
        entryRef.current = null;
        const heldWholeTake = previewBytesRef.current > 0
            && previewBytesRef.current <= PREVIEW_MAX_BYTES
            // Only a complete local copy is worth offering as playback; a
            // truncated one would mislead the respondent about what was captured.
            && previewChunksRef.current.length >= Math.floor((duration * 1000) / TIMESLICE_MS);

        const previewBlob = heldWholeTake
            ? new Blob(previewChunksRef.current, { type: entry.recorder?.mimeType || "audio/webm" })
            : null;

        previewChunksRef.current = [];
        previewBytesRef.current = 0;
        setFinalising(false);

        if (!ok) {
            setError(t(
                "Voice.UploadIncomplete",
                "The recording could not be fully uploaded. Please record again."
            ));
            onChange(null);
            return;
        }

        onChange({
            recordingUuid: entry.recordingUuid,
            duration,
            uploaded: true,
            previewBlob,
        });
    }, [finishTrack, onChange, t]);

    if (recording || finalising) {
        return (
            <div className="space-y-3">
                <div className="flex items-center gap-3">
                    <span className="flex h-3 w-3 flex-none">
                        <span className="h-3 w-3 animate-pulse rounded-full bg-red-600" />
                    </span>
                    <span className="text-lg font-medium tabular-nums text-gray-900">
                        {formatTime(elapsed)}
                    </span>
                    <button
                        type="button"
                        onClick={stop}
                        disabled={finalising}
                        className="inline-flex items-center gap-1.5 rounded-md bg-black px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
                    >
                        <StopIcon className="h-4 w-4" />
                        {finalising
                            ? t("Voice.Saving", "Saving…")
                            : t("Voice.Stop", "Stop")}
                    </button>
                </div>
                <p className="text-xs text-gray-500">
                    {t("Voice.Streaming", "Your answer is being saved as you speak — there is no time limit.")}
                </p>
                {warning && <p className="text-xs text-amber-700">{warning}</p>}
            </div>
        );
    }

    if (value?.uploaded) {
        return (
            <div className="space-y-3">
                {previewUrl ? (
                    <div className="flex items-center gap-3">
                        <audio
                            controls
                            src={previewUrl}
                            className="h-9 w-full max-w-sm"
                            onError={() => setWarning(t("Voice.PlaybackFailed", "Could not play the recording back, but it was saved."))}
                        />
                        {/* MediaRecorder's WebM output carries no duration header, so
                            the player's own timer often reads 0:00 or Infinity. Show
                            the length we measured while recording instead. */}
                        <span className="flex-none text-sm tabular-nums text-gray-500">
                            {formatTime(value.duration || 0)}
                        </span>
                    </div>
                ) : (
                    <p className="text-sm text-gray-700">
                        {t("Voice.SavedNoPreview", "Recording saved ({{time}}).", { time: formatTime(value.duration || 0) })}
                    </p>
                )}
                {warning && <p className="text-sm text-amber-700">{warning}</p>}
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => { setError(null); setWarning(null); onChange(null); }}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <ArrowPathIcon className="h-4 w-4" />
                        {t("Voice.Rerecord", "Record again")}
                    </button>
                    <span className="text-xs text-gray-500">
                        {t("Voice.WillTranscribe", "Your recording will be transcribed automatically.")}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-2">
            <button
                type="button"
                disabled={disabled}
                onClick={start}
                className="inline-flex items-center gap-2 rounded-md bg-black px-5 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
                <MicrophoneIcon className="h-5 w-5" />
                {t("Voice.Record", "Record your answer")}
            </button>
            {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
    );
}
